import { User, LogOut, CreditCard, CheckCircle, AlertCircle, Settings, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

export const UserMenu = ({ user, onLogout, onManageSubscription }) => {
  const navigate = useNavigate();
  const isSubscribed = user?.is_subscribed;
  const notificationsEnabled = user?.notification_settings?.enabled;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="flex items-center gap-2 text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"
          data-testid="user-menu-trigger"
        >
          <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center relative">
            <User className="w-4 h-4" />
            {notificationsEnabled && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-zinc-950" />
            )}
          </div>
          <span className="hidden sm:inline text-sm truncate max-w-[120px]">
            {user?.name || user?.email?.split('@')[0]}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-800" align="end">
        <DropdownMenuLabel className="text-zinc-300">
          <div className="flex flex-col">
            <span className="font-medium">{user?.name || 'Account'}</span>
            <span className="text-xs text-zinc-500 truncate">{user?.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-zinc-800" />
        
        {/* Subscription Status */}
        <DropdownMenuItem className="text-zinc-300 focus:bg-zinc-800 cursor-default" disabled>
          {isSubscribed ? (
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle className="w-4 h-4" />
              <span>Active Subscriber</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-400">
              <AlertCircle className="w-4 h-4" />
              <span>Not Subscribed</span>
            </div>
          )}
        </DropdownMenuItem>
        
        {isSubscribed && user?.subscription_end_date && (
          <DropdownMenuItem className="text-xs text-zinc-500 focus:bg-zinc-800 cursor-default" disabled>
            Renews: {new Date(user.subscription_end_date).toLocaleDateString()}
          </DropdownMenuItem>
        )}
        
        <DropdownMenuSeparator className="bg-zinc-800" />
        
        {/* Account Settings - for subscribed users */}
        {isSubscribed && (
          <DropdownMenuItem 
            className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-100 cursor-pointer"
            onClick={() => navigate('/account')}
            data-testid="account-settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Account Settings
          </DropdownMenuItem>
        )}
        
        {/* Notification status indicator */}
        {isSubscribed && (
          <DropdownMenuItem 
            className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-100 cursor-pointer"
            onClick={() => navigate('/account')}
          >
            <Bell className="w-4 h-4 mr-2" />
            Notifications {notificationsEnabled ? (
              <span className="ml-auto text-xs text-emerald-400">ON</span>
            ) : (
              <span className="ml-auto text-xs text-zinc-500">OFF</span>
            )}
          </DropdownMenuItem>
        )}
        
        {!isSubscribed && (
          <DropdownMenuItem 
            className="text-red-400 focus:bg-zinc-800 focus:text-red-300 cursor-pointer"
            onClick={onManageSubscription}
            data-testid="manage-subscription-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Subscribe Now
          </DropdownMenuItem>
        )}
        
        <DropdownMenuSeparator className="bg-zinc-800" />
        
        <DropdownMenuItem 
          className="text-zinc-400 focus:bg-zinc-800 focus:text-zinc-200 cursor-pointer"
          onClick={onLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
